import { createContext, useContext, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useWS } from './WSContext'

// Escopo global de mina: 'all' | 'none' | <id numérico>. Boot: URL ?mine= vence,
// depois localStorage, depois 'all'. Mudanças persistem nos dois.
const STORAGE_KEY = 'volis-mine-scope'

const ScopeContext = createContext(null)
export const useScope = () => useContext(ScopeContext)

function parseScope(raw) {
  if (raw == null || raw === '') return null
  if (raw === 'all' || raw === 'none') return raw
  if (!/^-?\d+$/.test(String(raw))) return null
  return Number(raw)
}

function initialScope() {
  const fromUrl = parseScope(new URLSearchParams(window.location.search).get('mine'))
  if (fromUrl != null) return fromUrl
  let stored = null
  try { stored = localStorage.getItem(STORAGE_KEY) } catch { /* storage indisponível */ }
  return parseScope(stored) ?? 'all'
}

export function ScopeProvider({ children }) {
  const { t } = useTranslation()
  const { mines } = useWS()
  const [mineScope, setMineScopeState] = useState(initialScope)

  const setMineScope = useCallback((scope) => {
    setMineScopeState(scope)
    try { localStorage.setItem(STORAGE_KEY, String(scope)) } catch { /* storage indisponível */ }
    const url = new URL(window.location.href)
    if (scope === 'all') url.searchParams.delete('mine')
    else url.searchParams.set('mine', String(scope))
    window.history.replaceState(window.history.state, '', url)
  }, [])

  // String pronta para anexar a querystrings ('' quando o escopo é global).
  const mineQS = mineScope === 'all' ? '' : `mine=${mineScope === 'none' ? 'none' : mineScope}`

  const matchesScope = useCallback((device) => {
    if (mineScope === 'all') return true
    if (mineScope === 'none') return device?.mineId == null
    return device?.mineId === mineScope
  }, [mineScope])

  // Mina apagada (ou id inválido na URL): o servidor cai para todas as minas quando
  // não reconhece o id, então o cliente tem que cair junto, senão a app fica filtrada
  // em nada para sempre, com o id preso no localStorage.
  useEffect(() => {
    if (typeof mineScope !== 'number' || mines.length === 0) return
    if (!mines.some(m => Number(m.id) === mineScope)) setMineScope('all')
  }, [mines, mineScope, setMineScope])

  const scopeLabel = useMemo(() => {
    if (mineScope === 'all') return t('common:scope.all_mines')
    if (mineScope === 'none') return t('common:scope.no_mine')
    const mine = mines.find(m => Number(m.id) === mineScope)
    return mine ? mine.name : `${t('common:scope.mine')} ${mineScope}`
  }, [mineScope, mines, t])

  return (
    <ScopeContext.Provider value={{ mineScope, setMineScope, mineQS, matchesScope, scopeLabel }}>
      {children}
    </ScopeContext.Provider>
  )
}
